import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ApiTestForm from './ApiTestForm';
import ApiLogPanel from './ApiLogPanel';
import DebugPanel from './DebugPanel';
import { logger } from '../utils/logger';

const { FiMonitor, FiDatabase, FiCode, FiSettings, FiActivity, FiLogOut, FiCpu } = FiIcons;

function ApiMonitorDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('ncb');
  const [logs, setLogs] = useState([]);
  const [isLogPanelOpen, setIsLogPanelOpen] = useState(true);

  useEffect(() => {
    const unsubscribe = logger.subscribe((updatedLogs) => {
      setLogs(updatedLogs);
    });
    return unsubscribe;
  }, []);

  const tabs = [
    { id: 'ncb', label: 'NoCodeBackend', icon: FiDatabase, color: 'text-blue-600' },
    { id: 'dropbox', label: 'Dropbox API', icon: FiCode, color: 'text-green-600' },
    { id: 'openai', label: 'OpenAI API', icon: FiCpu, color: 'text-purple-600' },
    { id: 'debug', label: 'Debug Panel', icon: FiSettings, color: 'text-gray-600' }
  ];

  const handleLogout = () => {
    logger.info('User initiated logout');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <SafeIcon icon={FiMonitor} className="text-primary-600 text-2xl" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">API Monitor Platform</h1>
              <p className="text-sm text-gray-500">Real-time API testing & logging</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setIsLogPanelOpen(!isLogPanelOpen)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                isLogPanelOpen ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiActivity} />
              <span>Logs ({logs.length})</span>
            </button>
            <button 
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={FiLogOut} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className={`grid gap-6 ${isLogPanelOpen ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex space-x-1 mb-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-primary-500 text-white' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon 
                      icon={tab.icon} 
                      className={activeTab === tab.id ? 'text-white' : tab.color} 
                    />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
              
              {activeTab === 'debug' ? (
                <DebugPanel />
              ) : (
                <ApiTestForm apiType={activeTab} />
              )}
            </div>
          </div>

          {isLogPanelOpen && (
            <motion.div 
              initial={{ opacity: 0, x: 300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 300 }}
              transition={{ duration: 0.3 }}
              className="lg:col-span-1"
            >
              <ApiLogPanel logs={logs} />
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}

export default ApiMonitorDashboard;